import { createAsyncSelectorResults } from 'async-selector-kit'
import { actions as appActions } from '../../../App/reducer'
import { getPortalCustomerProfileId } from '../../../App/selectors'
import { API_PATH_PORTAL_BOOTSTRAP } from '../../../../config'
import { Profile, ProfileValues, Subscription, Transaction } from '../../types'
import { getApiConfig, getLocalServerURL } from '../../../../utils/getApiConfig'
import { onIsLoading } from '../../../../utils/onLoading'
import { clientLogger } from '../../../../utils/observability'

export interface PortalBootstrapData {
  profile: Profile
  transactions: Transaction[]
  subscriptions: Subscription[]
}

const DEFAULT_PORTAL_BOOTSTRAP_DATA: PortalBootstrapData = {
  profile: ProfileValues,
  transactions: [],
  subscriptions: [],
}

const dispatchWindowStoreAction = (action: any) => {
  window.store?.dispatch(action)
}

const onBootstrapError = (message: string): PortalBootstrapData => {
  clientLogger.error('portal.bootstrap.error', { message })
  dispatchWindowStoreAction(appActions.onSubscriptionIds([]))
  dispatchWindowStoreAction(
    appActions.onAlert({
      severity: 'error',
      message,
    })
  )
  onIsLoading(false)
  return DEFAULT_PORTAL_BOOTSTRAP_DATA
}

export const [
  getPortalBootstrap,
  getPortalBootstrapLoading,
  getPortalBootstrapError,
  getPortalBootstrapForce,
] = createAsyncSelectorResults(
  {
    async: async (customerProfileId) => {
      onIsLoading(true)

      try {
        clientLogger.info('portal.bootstrap.requested', {
          customerProfileId: customerProfileId ?? undefined,
        })
        const config = await getApiConfig({
          body: customerProfileId != null ? { customerProfileId } : {},
        })

        if (config == null) {
          dispatchWindowStoreAction(appActions.onSubscriptionIds([]))
          onIsLoading(false)
          return DEFAULT_PORTAL_BOOTSTRAP_DATA
        }

        const response = await fetch(
          getLocalServerURL(API_PATH_PORTAL_BOOTSTRAP),
          config
        )

        if (!response.ok) {
          return onBootstrapError('Failed to fetch portal data')
        }

        const data = (await response.json()) as PortalBootstrapData
        if (data.profile.customerProfileId > 0) {
          dispatchWindowStoreAction(
            appActions.onCustomerProfileId(data.profile.customerProfileId)
          )
        }
        dispatchWindowStoreAction(
          appActions.onSubscriptionIds(data.profile.subscriptionIds)
        )
        clientLogger.info('portal.bootstrap.succeeded', {
          customerProfileId: data.profile.customerProfileId,
          subscriptionCount: data.subscriptions.length,
          transactionCount: data.transactions.length,
        })
        onIsLoading(false)
        return data
      } catch (error) {
        clientLogger.error('portal.bootstrap.exception', { error })
        return onBootstrapError('Failed to fetch portal data')
      }
    },
    id: 'fetch-portal-bootstrap',
    defaultValue: DEFAULT_PORTAL_BOOTSTRAP_DATA,
  },
  [getPortalCustomerProfileId]
)
